import { useCallback, useRef, useState } from 'react';
import { getId } from 'utils/objects';

import * as themes from './themes';
import { ETheme } from './types';

interface IThemeMapItem {
  theme: typeof themes.main;
  label: string;
  name: string;
}

type IThemeMap = Record<ETheme, IThemeMapItem>;

export const themeMap: IThemeMap = {
  [ETheme.MAIN]: { theme: themes.main, label: 'Main Theme', name: ETheme.MAIN },
};

interface IThemeOption {
  label: string;
  value: ETheme;
}

type IOptionsById = Record<ETheme, IThemeOption>;

const getSelectOptions = () => {
  const optionsById = {} as IOptionsById;

  const options = Object.entries(themeMap).map(([key, { label }]) => {
    const _value = key as unknown;
    const value = _value as ETheme;
    const option: IThemeOption = {
      label,
      value,
    };
    optionsById[value] = option;
    return option;
  });

  return {
    optionsById,
    options,
  };
};

export const initialState = {
  error: null,
  isLeftNavigationCollapsed: false,
  loading: false,
  // eslint-disable-next-line
  onChange: (a: any) => {},
  options: getSelectOptions().options,
  // eslint-disable-next-line
  setThemeId: (a: ETheme) => {},
  themeId: ETheme.MAIN,
  theme: themeMap[ETheme.MAIN].theme,
  // eslint-disable-next-line
  toggleLeftNavigationCollapse: () => {},
  value: {
    label: themeMap[ETheme.MAIN].label,
    value: ETheme.MAIN,
  } as IThemeOption,
};

const useTheme = () => {
  const [themeId, setThemeId] = useState<ETheme>(ETheme.MAIN);
  const [isLeftNavigationCollapsed, setIsLeftNavigationCollapsed] =
    useState(false);
  const onChangeHanderIdFactoryRef = useRef(getId());
  const onChangeHandlersRef = useRef<
    Record<number, ((value: ETheme) => void) | undefined>
  >({});

  const {
    current: { optionsById, options },
  } = useRef(getSelectOptions());

  const onChange = useCallback((formState) => {
    setThemeId(formState.value);
    Object.values(onChangeHandlersRef.current).forEach((callback) => {
      if (typeof callback === 'function') {
        callback(formState.value);
      }
    });
  }, []);

  const registerOnChangeHandler = useCallback((callback) => {
    const { current: counter } = onChangeHanderIdFactoryRef;
    const id = counter();
    onChangeHandlersRef.current[id] = callback;
  }, []);

  const removeOnChangeHandler = useCallback((id) => {
    onChangeHandlersRef.current[id] = undefined;
  }, []);

  const { theme } = themeMap[themeId];
  const themeRef = useRef(theme);

  const toggleLeftNavigationCollapse = useCallback(() => {
    setIsLeftNavigationCollapsed((prev) => {
      const collapsedState = {
        leftPadding: '0rem',
        width: '0rem',
      };
      const expandedState = theme.layout.leftNav;
      let finalState = expandedState;
      if (!prev) {
        finalState = collapsedState;
      }
      themeRef.current = {
        ...theme,
        layout: {
          ...theme.layout,
          leftNav: finalState,
        },
      };
      return !prev;
    });
  }, [theme]);

  return {
    error: null,
    isLeftNavigationCollapsed,
    loading: false,
    onChange,
    options,
    registerOnChangeHandler,
    removeOnChangeHandler,
    setThemeId,
    themeId,
    theme: themeRef.current,
    toggleLeftNavigationCollapse,
    value: optionsById[themeId],
  };
};

export default useTheme;
